import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { qualifier, sumArrayLike, typedObjectKeys, filterMap } from "$root/utils";
import type { InvestmentExposureResponseExposureTypeEnum } from "$root/widgets-architecture/widgets/ExposureEvolve/InvestmentExposureResponseExposureTypeEnum";
import {
	type PortfolioMetricTypes,
	type UserInstrumentClassificationDto,
	CommentaryTemplateControllerApiFactory,
	InstrumentsClassificationsControllerV1ApiFactory,
} from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import {
	type CollapsibleHeaderProps,
	type OptionWithChildren,
	Collapsible,
	DebouncedSearchInput,
	DefaultCollapsibleHeader,
	DraggableList,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	Autocomplete,
	type AutocompleteHandle,
	Button,
	Checkbox,
	IconButton,
	Label,
} from "@mdotm/shadcn/mdotm/components";
import Draggable from "$root/components/common/Draggable";
import type { MaybePromise } from "@mdotm/utils/promise";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { groupBy } from "@mdotm/utils/collections";
import { ForEach } from "@mdotm/utils/react-extra";
import { Map } from "immutable";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useFieldArray, useForm, type UseFieldArrayAppend, type UseFieldArrayUpdate } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	type CompositionColumnPreference,
	type ExAntePortfolioMetricType,
	type ReportTemplateVariant,
	itemToLabelMap,
	type ReportTemplateItemFor,
	type ReportTemplateItemMap,
} from "../../../functional-areas/reports/report-latest";
import { Divider } from "$root/components/Divider";
import { investmentCustomAttributesRepository } from "$root/functional-areas/customAttributes/repository";

export type SetConfigurationReportTemplateHelper<K extends keyof ReportTemplateItemMap> = {
	templateVariant: ReportTemplateVariant;
	configuration: ReportTemplateItemFor<K>;
	onChange(newConfiguration: ReportTemplateItemFor<K>): void;
	onCancel(): void;
};

export function SideHeaderContent(props: {
	title: string;
	onCancel?(): void;
	onSubmit(): MaybePromise<void>;
}): React.JSX.Element {
	const { t } = useTranslation();
	return (
		<div className={`flex items-center border-b border-solid border-b-[color:var(--palette-N100)] p-4`}>
			<Text type="Body/XL/Bold" as="p" className="grow h-fit line-clamp-2">
				{props.title}
			</Text>
			<div className="flex justify-end py-2 px-2 space-x-4 bg-white">
				<Button
					palette="tertiary"
					onClick={props.onCancel}
					data-qualifier={qualifier.reportCustomisation.builder.widgetDetails.cancel}
				>
					{t("BUTTON.CANCEL")}
				</Button>
				<Button
					palette="primary"
					onClickAsync={props.onSubmit}
					data-qualifier={qualifier.reportCustomisation.builder.widgetDetails.save}
				>
					{t("BUTTON.SAVE")}
				</Button>
			</div>
		</div>
	);
}

export function SideBarContent(props: {
	title: string;
	onSubmit(): MaybePromise<void>;
	onCancel(): void;
	children: ReactNode;
}): React.JSX.Element {
	// bg-[color:var(--palette-N20)]
	return (
		<div className="grow bg-white overflow-y-auto pb-6">
			<SideHeaderContent title={props.title} onSubmit={props.onSubmit} onCancel={props.onCancel} />
			<div className={`px-4 pt-4 border-t border-solid border-t-[color:var(--palette-N100)]`}>{props.children}</div>
		</div>
	);
}

export function Summary(props: SetConfigurationReportTemplateHelper<"summary">): React.JSX.Element {
	const { id, kind, standardFields, customAttributes = [] } = props.configuration;
	const availableAttributesQuery = investmentCustomAttributesRepository.customAttributes.useQuery();
	const { t } = useTranslation();
	const { control, handleSubmit, formState, watch, setValue } = useForm({
		defaultValues: {
			standardFields,
			customAttributes,
		},
	});

	const availableAttributesMap = useMemo(
		() => Map(availableAttributesQuery.data?.map((available) => [available.attributeUuid!, available])),
		[availableAttributesQuery.data],
	);

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<div>
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ForEach collection={typedObjectKeys(standardFields)}>
					{(key) => (
						<div>
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name={`standardFields.${key}`}
								className="mb-1"
								data-qualifier={qualifier.reportCustomisation.modules.summary.metadata(key)}
							>
								{t(`PORTFOLIO_SUMMARY.${key}`)}
							</FormFields.Checkbox>
						</div>
					)}
				</ForEach>
			</div>
			<ReactQueryWrapperBase query={availableAttributesQuery}>
				{(availableAttributes) => {
					const customAttributes = watch("customAttributes");
					const filledCustomAttributes = [
						// all the ones in the report settings, except those which don't exist anymore
						...filterMap(customAttributes, (c) => {
							if (availableAttributesMap.has(c.uuid)) {
								return [c] as const;
							}
							return [];
						}),
						// all upstream ones, except those already in the report
						...filterMap(availableAttributes, (available) => {
							if (!customAttributes.some((c) => c.uuid === available.attributeUuid)) {
								return [
									{
										uuid: available.attributeUuid!,
										enabled: false,
									},
								];
							}
							return [];
						}),
					];
					return (
						availableAttributes &&
						availableAttributes.length > 0 && (
							<div>
								<Divider className="my-4" />
								<p className="mb-4 font-semibold">Select the custom attributes you want to display</p>
								<DraggableList
									items={filledCustomAttributes}
									itemKey={(item) => item.uuid}
									onReorder={(x) => setValue("customAttributes", x)}
								>
									{({ dragHandleProps, isDragging, draggableProps, item: { uuid, enabled }, innerRef }) => {
										const name = availableAttributesMap.get(uuid)?.name ?? "";
										return (
											<div
												style={{ borderColor: themeCSSVars.palette_N50 }}
												className={toClassName({
													"relative flex flex-row items-center min-w-0 bg-white transition-shadow border-b gap-2 px-2 h-8": true,
													"shadow-lg": isDragging,
													"border-t": isDragging,
												})}
												{...draggableProps}
												ref={innerRef}
											>
												<div
													{...dragHandleProps}
													className={toClassName({ invisible: !draggableProps || !dragHandleProps })}
												>
													<svg
														role="presentation"
														width="16"
														height="16"
														viewBox="0 0 16 16"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															fillRule="evenodd"
															clipRule="evenodd"
															d="M6.04181 3.2944L7.96096 1.37506C8.01677 1.31944 8.10704 1.31944 8.16286 1.37506L10.082 3.2944C10.1228 3.3352 10.1349 3.3966 10.113 3.44991C10.0909 3.50323 10.0389 3.53807 9.98115 3.53807H6.14267C6.08493 3.53807 6.03277 3.50323 6.01083 3.44991C5.98888 3.3966 6.00101 3.3352 6.04181 3.2944ZM8.16305 14.8297C8.10724 14.8853 8.01697 14.8853 7.96115 14.8297L6.04181 12.9103C6.00101 12.8695 5.98888 12.8081 6.01083 12.7548C6.03296 12.7015 6.08512 12.6667 6.14286 12.6667H9.98134C10.0391 12.6667 10.091 12.7015 10.1132 12.7548C10.1351 12.8081 10.123 12.8695 10.0822 12.9103L8.16305 14.8297ZM14.3446 6.00001H1.97822C1.62186 6.00001 1.33337 6.29415 1.33337 6.65052C1.33337 7.00688 1.33337 7.30102 1.97822 7.30102H14.3333C14.701 7.30102 14.9895 7.01254 14.9895 6.65052C14.9895 6.28849 14.701 6.00001 14.3446 6.00001ZM1.97822 9.33334H14.3446C14.701 9.33334 14.9895 9.62183 14.9895 9.98385C14.9895 10.3459 14.701 10.6344 14.3333 10.6344H1.97822C1.33337 10.6344 1.33337 10.3402 1.33337 9.98385C1.33337 9.62748 1.62186 9.33334 1.97822 9.33334Z"
															fill="#8792AB"
														/>
													</svg>
												</div>
												<div>
													<Checkbox
														className="mb-1"
														data-qualifier={qualifier.reportCustomisation.modules.summary.metadata(uuid)}
														checked={enabled}
														onChange={(checked) => {
															setValue(
																"customAttributes",
																filledCustomAttributes.map((x) => (x.uuid === uuid ? { ...x, enabled: checked } : x)),
															);
														}}
													>
														{name}
													</Checkbox>
												</div>
											</div>
										);
									}}
								</DraggableList>
							</div>
						)
					);
				}}
			</ReactQueryWrapperBase>
		</SideBarContent>
	);
}

export function Disclaimer(props: SetConfigurationReportTemplateHelper<"disclaimer">): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, formState, watch } = useForm({
		defaultValues: opts,
	});

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<p className="mb-4 font-semibold">Customize the title you want to display</p>
			<FormFields.Checkbox
				control={control}
				formState={formState}
				name="content.enabled"
				className="mb-2"
				data-qualifier={qualifier.reportCustomisation.modules.disclaimer.enableCustomTitle}
			>
				Custom Text
			</FormFields.Checkbox>

			<FormFields.Text
				control={control}
				formState={formState}
				label="Title"
				name="content.title"
				className="mb-2"
				disabled={!watch("content.enabled")}
				data-qualifier={qualifier.reportCustomisation.modules.disclaimer.title}
			/>

			<FormFields.TextArea
				control={control}
				formState={formState}
				label="Text"
				name="content.description"
				className="mb-4 [&>textarea]:resize-none"
				disabled={!watch("content.enabled")}
				rows={5}
				data-qualifier={qualifier.reportCustomisation.modules.disclaimer.description}
			/>

			<FormFields.Checkbox
				control={control}
				formState={formState}
				name="contact.enabled"
				className="mb-2"
				data-qualifier={qualifier.reportCustomisation.modules.disclaimer.enableContacts}
			>
				Contacts
			</FormFields.Checkbox>
			<FormFields.TextArea
				control={control}
				formState={formState}
				label=""
				name="contact.description"
				className="mb-2 [&>textarea]:resize-none"
				disabled={!watch("contact.enabled")}
				rows={5}
				data-qualifier={qualifier.reportCustomisation.modules.disclaimer.contacts}
			/>
		</SideBarContent>
	);
}

export function Cover(props: SetConfigurationReportTemplateHelper<"cover">): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<p className="mb-4 font-semibold">Customize the title you want to display</p>
			<FormFields.Text
				label="Title"
				name="title"
				control={control}
				formState={formState}
				data-qualifier={qualifier.reportCustomisation.modules.cover.title}
			/>
		</SideBarContent>
	);
}

type MetricId =
	| "PERFORMANCE"
	| "SORTINO"
	| "MAX_DRAWDOWN"
	| "MAX_DRAWDOWN_TIME_TO_RECOVERY"
	| "VOLATILITY"
	| "EFFICIENCY_RATIO"
	| "SCORE"
	| "SHARPE_RATIO"
	| "CALMAR_RATIO"
	| "ALPHA"
	| "BETA"
	| "BATTING_AVERAGE"
	| "UPSIDE_CAPTURE_RATIO"
	| "DOWNSIDE_CAPTURE_RATIO"
	| "CORRELATION";

const groupedPortfolioMetricsByTypes = [
	{
		label: "Volatility",
		value: "VOLATILITY",
		children: [
			"VOLATILITY_SINCE_INCEPTION",
			"VOLATILITY_YTD",
			"VOLATILITY_QTD",
			"VOLATILITY_MTD",
			"VOLATILITY_1Y",
			"VOLATILITY_6M",
			"VOLATILITY_3M",
			"VOLATILITY_1M",
			"VOLATILITY_1W",
		],
	},
	{
		label: "Efficiency ratio",
		value: "EFFICIENCY_RATIO",
		children: [
			"EFFICIENCY_RATIO_SINCE_INCEPTION",
			"EFFICIENCY_RATIO_YTD",
			"EFFICIENCY_RATIO_QTD",
			"EFFICIENCY_RATIO_MTD",
			"EFFICIENCY_RATIO_1Y",
			"EFFICIENCY_RATIO_6M",
			"EFFICIENCY_RATIO_3M",
			"EFFICIENCY_RATIO_1M",
			"EFFICIENCY_RATIO_1W",
		],
	},
	{
		label: "Max drawdown",
		value: "MAX_DRAWDOWN",
		children: [
			"MAX_DRAWDOWN_SINCE_INCEPTION",
			"MAX_DRAWDOWN_YTD",
			"MAX_DRAWDOWN_QTD",
			"MAX_DRAWDOWN_MTD",
			"MAX_DRAWDOWN_1Y",
			"MAX_DRAWDOWN_6M",
			"MAX_DRAWDOWN_3M",
			"MAX_DRAWDOWN_1M",
			"MAX_DRAWDOWN_1W",
		],
	},
	{
		label: "Max drawdown Time to recovery",
		value: "MAX_DRAWDOWN_TIME_TO_RECOVERY",
		children: [
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_SINCE_INCEPTION",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_YTD",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_QTD",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_MTD",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_1Y",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_6M",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_3M",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_1M",
			"MAX_DRAWDOWN_TIME_TO_RECOVERY_1W",
		],
	},
	{
		label: "Performance",
		value: "PERFORMANCE",
		children: [
			"PERFORMANCE_SINCE_INCEPTION",
			"PERFORMANCE_YTD",
			"PERFORMANCE_QTD",
			"PERFORMANCE_MTD",
			"PERFORMANCE_1Y",
			"PERFORMANCE_6M",
			"PERFORMANCE_3M",
			"PERFORMANCE_1M",
			"PERFORMANCE_1W",
			"PERFORMANCE_1D",
		],
	},
	{
		label: "Sortino",
		value: "SORTINO",
		children: [
			"SORTINO_SINCE_INCEPTION",
			"SORTINO_YTD",
			"SORTINO_QTD",
			"SORTINO_MTD",
			"SORTINO_1Y",
			"SORTINO_6M",
			"SORTINO_3M",
			"SORTINO_1M",
			"SORTINO_1W",
		],
	},
	{
		label: "Sharpe Ratio",
		value: "SHARPE_RATIO",
		children: [
			"SHARPE_RATIO_SINCE_INCEPTION",
			"SHARPE_RATIO_YTD",
			"SHARPE_RATIO_QTD",
			"SHARPE_RATIO_MTD",
			"SHARPE_RATIO_1Y",
			"SHARPE_RATIO_6M",
			"SHARPE_RATIO_3M",
			"SHARPE_RATIO_1M",
			"SHARPE_RATIO_1W",
		],
	},
	{
		label: "Calmar Ratio",
		value: "CALMAR_RATIO",
		children: [
			"CALMAR_RATIO_SINCE_INCEPTION",
			"CALMAR_RATIO_YTD",
			"CALMAR_RATIO_QTD",
			"CALMAR_RATIO_MTD",
			"CALMAR_RATIO_1Y",
			"CALMAR_RATIO_6M",
			"CALMAR_RATIO_3M",
			"CALMAR_RATIO_1M",
			"CALMAR_RATIO_1W",
		],
	},
	{
		label: "Alpha",
		value: "ALPHA",
		children: [
			"ALPHA_SINCE_INCEPTION",
			"ALPHA_YTD",
			"ALPHA_QTD",
			"ALPHA_MTD",
			"ALPHA_1Y",
			"ALPHA_6M",
			"ALPHA_3M",
			"ALPHA_1M",
		],
	},
	{
		label: "Beta",
		value: "BETA",
		children: ["BETA_SINCE_INCEPTION", "BETA_YTD", "BETA_QTD", "BETA_MTD", "BETA_1Y", "BETA_6M", "BETA_3M", "BETA_1M"],
	},
	{
		label: "Batting Average",
		value: "BATTING_AVERAGE",
		children: [
			"BATTING_AVERAGE_SINCE_INCEPTION",
			"BATTING_AVERAGE_YTD",
			"BATTING_AVERAGE_QTD",
			"BATTING_AVERAGE_MTD",
			"BATTING_AVERAGE_1Y",
			"BATTING_AVERAGE_6M",
			"BATTING_AVERAGE_3M",
			"BATTING_AVERAGE_1M",
		],
	},
	{
		label: "Upside Capture Ratio",
		value: "UPSIDE_CAPTURE_RATIO",
		children: [
			"UPSIDE_CAPTURE_RATIO_SINCE_INCEPTION",
			"UPSIDE_CAPTURE_RATIO_YTD",
			"UPSIDE_CAPTURE_RATIO_QTD",
			"UPSIDE_CAPTURE_RATIO_MTD",
			"UPSIDE_CAPTURE_RATIO_1Y",
			"UPSIDE_CAPTURE_RATIO_6M",
			"UPSIDE_CAPTURE_RATIO_3M",
			"UPSIDE_CAPTURE_RATIO_1M",
		],
	},
	{
		label: "Downside Capture Ratio",
		value: "DOWNSIDE_CAPTURE_RATIO",
		children: [
			"DOWNSIDE_CAPTURE_RATIO_SINCE_INCEPTION",
			"DOWNSIDE_CAPTURE_RATIO_YTD",
			"DOWNSIDE_CAPTURE_RATIO_QTD",
			"DOWNSIDE_CAPTURE_RATIO_MTD",
			"DOWNSIDE_CAPTURE_RATIO_1Y",
			"DOWNSIDE_CAPTURE_RATIO_6M",
			"DOWNSIDE_CAPTURE_RATIO_3M",
			"DOWNSIDE_CAPTURE_RATIO_1M",
		],
	},
	{
		label: "Correlation",
		value: "CORRELATION",
		children: [
			"CORRELATION_SINCE_INCEPTION",
			"CORRELATION_YTD",
			"CORRELATION_QTD",
			"CORRELATION_MTD",
			"CORRELATION_1Y",
			"CORRELATION_6M",
			"CORRELATION_3M",
			"CORRELATION_1M",
		],
	},
] satisfies Array<{
	label: string;
	value: MetricId;
	children: Array<PortfolioMetricTypes>;
}>;

type PortfolioMetricsUseFormProps = {
	preferences: Array<{ key: PortfolioMetricTypes; classificationUuid?: string; enabled: boolean }>;
	showBenchmark: boolean;
};

type PortfolioMetricsGroup = {
	label: string;
	group: {
		id: MetricId;
		label: string;
	};
	value: { key: PortfolioMetricTypes; classificationUuid?: string };
};
function deriveGroupOrder(preferences: Array<{ metricType: PortfolioMetricTypes; enabled: boolean }>): MetricId[] {
	const seen = new Set<MetricId>();
	const order: MetricId[] = [];

	for (const pref of preferences) {
		const group = groupedPortfolioMetricsByTypes.find((g) =>
			(g.children as readonly string[]).includes(pref.metricType),
		);
		if (group && !seen.has(group.value)) {
			seen.add(group.value);
			order.push(group.value);
		}
	}

	for (const group of groupedPortfolioMetricsByTypes) {
		if (!seen.has(group.value)) {
			order.push(group.value);
		}
	}

	return order;
}

/**
 * The padded body of a metric group's collapsible, shared by the ex-post and ex-ante panels. It
 * lives at module scope so its identity is stable: the panels re-render on every keystroke and
 * every checkbox, and a per-render identity would remount the checkbox list underneath it.
 */
function PortfolioMetricsCollapsibleContent(props: { children: ReactNode }): React.JSX.Element {
	return <div className="p-2">{props.children}</div>;
}

export function PortfolioMetrics(props: SetConfigurationReportTemplateHelper<"portfolioMetrics">): React.JSX.Element {
	const { kind, id, preferences, showBenchmark } = props.configuration;
	const [searchTerm, setSearchTerm] = useState("");
	const [groupOrder, setGroupOrder] = useState<MetricId[]>(() => deriveGroupOrder(preferences));
	const { t } = useTranslation();
	const { control, handleSubmit, formState, watch } = useForm<PortfolioMetricsUseFormProps>({
		defaultValues: {
			preferences: groupedPortfolioMetricsByTypes.flatMap((group) =>
				group.children.map((metricType) => {
					const existing = preferences.find((x) => x.metricType === metricType);
					return {
						enabled: existing?.enabled ?? false,
						key: metricType,
					};
				}),
			),
			showBenchmark,
		},
	});

	const portfolioMetricsPreferences = groupedPortfolioMetricsByTypes.flatMap((group) => {
		const optionsWithGroup = group.children.map(
			(metricType): PortfolioMetricsGroup => ({
				label: t(`PERFORMANCE_METRICS.METRICS.FULL.${metricType}`),
				value: { key: metricType },
				group: { id: group.value, label: group.label },
			}),
		);
		return optionsWithGroup;
	});

	const currentMetricsPreferencesControl = useFieldArray<PortfolioMetricsUseFormProps, "preferences", "id">({
		control,
		name: "preferences",
		keyName: "id",
	});

	function selectionToLabel(size: number) {
		return size > 0 ? `(${size})` : "";
	}

	const groupedRealizedMetrics = useMemo(
		() => groupBy(portfolioMetricsPreferences, (x) => x.group.id),
		[portfolioMetricsPreferences],
	);

	const observedPreference = watch("preferences");
	function findPreference(list: PortfolioMetricsUseFormProps["preferences"], valueToFind: PortfolioMetricsGroup) {
		return list.find(
			(x) => x.key === valueToFind.value.key && x.classificationUuid === valueToFind.value.classificationUuid,
		);
	}

	function findPreferenceIndex(list: PortfolioMetricsUseFormProps["preferences"], valueToFind: PortfolioMetricsGroup) {
		return list.findIndex(
			(x) => x.key === valueToFind.value.key && x.classificationUuid === valueToFind.value.classificationUuid,
		);
	}

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) =>
					props.onChange({
						id,
						kind,
						showBenchmark: payload.showBenchmark,
						preferences: groupOrder.flatMap((groupId) => {
							const group = groupedPortfolioMetricsByTypes.find((g) => g.value === groupId);
							if (!group) {
								return [];
							}
							return group.children.map((metricType) => {
								const pref = payload.preferences.find((x) => x.key === metricType);
								return {
									enabled: pref?.enabled ?? false,
									metricType,
									metricParameter: {
										classification: pref?.classificationUuid
											? { classificationUuid: pref.classificationUuid }
											: undefined,
									},
								};
							});
						}),
					}),
				)();
			}}
			onCancel={props.onCancel}
		>
			<Text className="mb-2" as="p" type="Body/M/Medium">
				Select the elements you want to display
			</Text>
			<DebouncedSearchInput
				data-qualifier={qualifier.reportCustomisation.modules.portfolioMetrics.searchBar}
				className="mb-2"
				timeout={10}
				externalQuery={searchTerm}
				placeholder="Search for metric"
				onChange={setSearchTerm}
			/>
			<div className="flex flex-col space-y- mb-4">
				<Draggable
					items={
						searchTerm !== ""
							? groupOrder.filter((groupId) =>
									(groupedRealizedMetrics[groupId] ?? []).some((item) =>
										item.label.toLowerCase().includes(searchTerm.toLowerCase()),
									),
								)
							: groupOrder
					}
					itemKey={(id) => id}
					className="mb-2"
					onReorder={setGroupOrder}
					disabled={searchTerm !== ""}
				>
					{({ data: groupId, dragIcon }) => {
						const items = groupedRealizedMetrics[groupId] ?? [];
						const headerLabel = `${items.at(0)?.group.label ?? ""} ${selectionToLabel(
							sumArrayLike(items, (metric) => (findPreference(observedPreference, metric)?.enabled ? 1 : 0)),
						)}`;
						const collapsibleHeader = (headerProps: CollapsibleHeaderProps) => (
							<DefaultCollapsibleHeader {...headerProps}>
								<div className="flex items-center gap-2">
									{dragIcon}
									{headerLabel}
								</div>
							</DefaultCollapsibleHeader>
						);
						return (
							<Collapsible
								className="grow"
								header={collapsibleHeader}
								data-qualifier={qualifier.reportCustomisation.modules.portfolioMetrics.toggleMacroMetric("MaxDrawDown")}
								expand={searchTerm !== ""}
							>
								<PortfolioMetricsCollapsibleContent>
									<ForEach
										collection={items.filter((metric) => metric.label.toLowerCase().includes(searchTerm.toLowerCase()))}
									>
										{(metric) => (
											<div>
												<Checkbox
													className="mb-1"
													checked={findPreference(observedPreference, metric)?.enabled ?? false}
													onChange={(newValue) => {
														const preferenceIndex = findPreferenceIndex(observedPreference, metric);
														if (preferenceIndex >= 0) {
															currentMetricsPreferencesControl.update(preferenceIndex, {
																key: metric.value.key,
																enabled: newValue,
																classificationUuid: metric.value.classificationUuid,
															});
														} else {
															currentMetricsPreferencesControl.append({
																key: metric.value.key,
																enabled: newValue,
																classificationUuid: metric.value.classificationUuid,
															});
														}
													}}
													data-qualifier={qualifier.reportCustomisation.modules.portfolioMetrics.metric(
														metric.value.key +
															(metric.value.classificationUuid ? `/${metric.value.classificationUuid}` : ""),
													)}
												>
													{metric.label}
												</Checkbox>
											</div>
										)}
									</ForEach>
								</PortfolioMetricsCollapsibleContent>
							</Collapsible>
						);
					}}
				</Draggable>
			</div>
			<div className={`mt-4 pt-4 -mx-4 border-t border-solid border-t-[color:var(--palette-N100)]`}>
				<div className="px-4">
					<p className="mb-2 font-semibold">Compare</p>
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="showBenchmark"
						data-qualifier={qualifier.reportCustomisation.modules.portfolioMetrics.showBenchmark}
					>
						Show benchmark
					</FormFields.Checkbox>
				</div>
			</div>
		</SideBarContent>
	);
}

type ExAntePortfolioMetricsFormValues = {
	preferences: Array<{
		metricType: ExAntePortfolioMetricType;
		enabled: boolean;
		metricParameter?: { classification?: { classificationUuid: string; option?: string } };
	}>;
	showBenchmark: boolean;
};

const EXANTE_METRIC_GROUPS = [
	{ label: "Volatility", metricTypes: ["VOLATILITY_3Y"] as const },
	{ label: "Diversification ratio", metricTypes: ["DIVERSIFICATION_RATIO_3Y"] as const },
	{ label: "Efficiency ratio", metricTypes: ["EFFICIENCY_RATIO_3Y"] as const },
	{ label: "Max drawdown", metricTypes: ["MAX_DRAWDOWN_3Y"] as const },
	{ label: "Historical Var", metricTypes: ["HISTORICAL_VAR_95_3Y"] as const },
	{ label: "Return", metricTypes: ["RETURN_3Y"] as const },
	{ label: "Tracking error", metricTypes: ["TRACKING_ERROR_3Y"] as const },
	{ label: "Risk constraint", metricTypes: ["VAR", "VOLATILITY", "TRACKING_ERROR"] as const },
] as const;

function createExAntePreferencesHelpers(
	fields: ExAntePortfolioMetricsFormValues["preferences"],
	append: UseFieldArrayAppend<ExAntePortfolioMetricsFormValues, "preferences">,
	update: UseFieldArrayUpdate<ExAntePortfolioMetricsFormValues, "preferences">,
) {
	return {
		toggle(metricType: ExAntePortfolioMetricType, enabled: boolean) {
			const idx = fields.findIndex((f) => f.metricType === metricType);
			if (idx >= 0) {
				update(idx, { ...fields[idx], enabled });
			} else {
				append({ metricType, enabled });
			}
		},
		toggleScore(classificationUuid: string, enabled: boolean) {
			const idx = fields.findIndex(
				(f) =>
					f.metricType === "SCORE_AVERAGE" &&
					f.metricParameter?.classification?.classificationUuid === classificationUuid,
			);
			if (idx >= 0) {
				update(idx, { ...fields[idx], enabled });
			} else {
				append({ metricType: "SCORE_AVERAGE", enabled, metricParameter: { classification: { classificationUuid } } });
			}
		},
		findScore(classificationUuid: string) {
			return fields.find(
				(f) =>
					f.metricType === "SCORE_AVERAGE" &&
					f.metricParameter?.classification?.classificationUuid === classificationUuid,
			);
		},
	};
}

export function ExAntePortfolioMetrics(
	props: SetConfigurationReportTemplateHelper<"exAntePortfolioMetrics">,
): React.JSX.Element {
	const { kind, id, preferences, showBenchmark } = props.configuration;
	const { t } = useTranslation();
	const { control, handleSubmit, formState } = useForm<ExAntePortfolioMetricsFormValues>({
		defaultValues: { preferences, showBenchmark },
	});

	const preferencesControl = useFieldArray<ExAntePortfolioMetricsFormValues, "preferences", "id">({
		control,
		name: "preferences",
		keyName: "id",
	});

	const helpers = createExAntePreferencesHelpers(
		preferencesControl.fields,
		preferencesControl.append,
		preferencesControl.update,
	);

	const userClassifications = useAtomValue(userClassifications$);
	const scoreClassifications =
		userClassifications?.flatMap((classification) => {
			if (classification.fieldType === "SCORE" && classification.classificationUuid) {
				return [{ classificationUuid: classification.classificationUuid, classificationName: classification.name }];
			}
			return [];
		}) ?? [];

	function selectionToLabel(n: number) {
		return n > 0 ? `(${n})` : "";
	}

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) =>
					props.onChange({
						id,
						kind,
						showBenchmark: payload.showBenchmark,
						preferences: payload.preferences,
					}),
				)();
			}}
			onCancel={props.onCancel}
		>
			<p className="mb-4 font-semibold">Select the elements you want to display</p>
			<ForEach collection={EXANTE_METRIC_GROUPS.filter((g) => g.metricTypes.length === 1)}>
				{(group) => (
					<ForEach collection={group.metricTypes}>
						{(metricType) => (
							<div className="mb-1.5">
								<Checkbox
									checked={preferencesControl.fields.find((f) => f.metricType === metricType)?.enabled ?? false}
									onChange={(enabled) => helpers.toggle(metricType, enabled)}
									data-qualifier={qualifier.reportCustomisation.modules.exAntePortfolioMetrics.metric(metricType)}
								>
									{t(`EXANTE_METRICS.METRICS.${metricType}`)}
								</Checkbox>
							</div>
						)}
					</ForEach>
				)}
			</ForEach>

			{EXANTE_METRIC_GROUPS.some((g) => g.metricTypes.length > 1) && <div className="mt-4" />}

			<ForEach collection={EXANTE_METRIC_GROUPS.filter((g) => g.metricTypes.length > 1)}>
				{(group) => {
					const enabledCount = group.metricTypes.filter(
						(mt) => preferencesControl.fields.find((f) => f.metricType === mt)?.enabled,
					).length;
					return (
						<div className="mb-1.5">
							<Collapsible
								header={`${group.label} ${selectionToLabel(enabledCount)}`}
								data-qualifier={qualifier.reportCustomisation.modules.exAntePortfolioMetrics.toggleMacroMetric(
									group.label,
								)}
							>
								<PortfolioMetricsCollapsibleContent>
									<ForEach collection={group.metricTypes}>
										{(metricType) => (
											<div>
												<Checkbox
													className="mb-px"
													checked={preferencesControl.fields.find((f) => f.metricType === metricType)?.enabled ?? false}
													onChange={(enabled) => helpers.toggle(metricType, enabled)}
													data-qualifier={qualifier.reportCustomisation.modules.exAntePortfolioMetrics.metric(
														metricType,
													)}
												>
													{t(`EXANTE_METRICS.METRICS.${metricType}`)}
												</Checkbox>
											</div>
										)}
									</ForEach>
								</PortfolioMetricsCollapsibleContent>
							</Collapsible>
						</div>
					);
				}}
			</ForEach>

			{scoreClassifications.length > 0 && (
				<Collapsible
					header={`Scores & metrics ${selectionToLabel(
						sumArrayLike(scoreClassifications, (metric) =>
							helpers.findScore(metric.classificationUuid)?.enabled ? 1 : 0,
						),
					)}`}
					data-qualifier={qualifier.reportCustomisation.modules.exAntePortfolioMetrics.toggleMacroMetric(
						"CustomMetrics",
					)}
					expand
				>
					<PortfolioMetricsCollapsibleContent>
						<ForEach collection={scoreClassifications}>
							{(metric) => {
								const isEnabled = helpers.findScore(metric.classificationUuid)?.enabled ?? false;
								return (
									<div>
										<Checkbox
											className="mb-1"
											checked={isEnabled}
											onChange={(newValue) => {
												helpers.toggleScore(metric.classificationUuid, newValue);
											}}
											data-qualifier={qualifier.reportCustomisation.modules.exAntePortfolioMetrics.metric(
												`SCORE_AVERAGE/${metric.classificationUuid}`,
											)}
										>
											{`Avg. ${metric.classificationName}`}
										</Checkbox>
									</div>
								);
							}}
						</ForEach>
					</PortfolioMetricsCollapsibleContent>
				</Collapsible>
			)}

			<div className={`mt-4 pt-4 -mx-4 border-t border-solid border-t-[color:var(--palette-N100)]`}>
				<div className="px-4">
					<p className="mb-2 font-semibold">Compare</p>
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="showBenchmark"
						data-qualifier={qualifier.reportCustomisation.modules.exAntePortfolioMetrics.showBenchmark}
					>
						Show benchmark
					</FormFields.Checkbox>
				</div>
			</div>
		</SideBarContent>
	);
}

export function FreeText(props: SetConfigurationReportTemplateHelper<"freeText">): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<p className="mb-4 font-semibold">Customize the description you want to display</p>
			<FormFields.TextArea
				label="Description"
				name="content"
				control={control}
				formState={formState}
				data-qualifier={qualifier.reportCustomisation.modules.freeText}
			/>
		</SideBarContent>
	);
}

export function Commentary(props: SetConfigurationReportTemplateHelper<"commentary">): React.JSX.Element {
	const { kind, id, templateUuid, ...opts } = props.configuration;
	const { control, handleSubmit, formState, getValues, setValue, watch } = useForm({
		defaultValues: { templateUuid, ...opts },
	});

	const commentaryTemplateApi = useApiGen(CommentaryTemplateControllerApiFactory);

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) =>
					props.onChange({
						id,
						kind,
						...payload,
					}),
				)();
			}}
			onCancel={props.onCancel}
		>
			<div className="flex flex-col gap-4">
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ReactQueryWrapper
					queryKey={["getCommentaryTemplateList"]}
					queryFn={async () => {
						const templates = await axiosExtract(commentaryTemplateApi.getTemplateList());
						return (
							templates.flatMap((x) => (x.name && x.uuid && x.visible ? [{ label: x.name, value: x.uuid }] : [])) ?? []
						);
					}}
					onSuccess={(opt) => {
						if (getValues("templateUuid") === undefined) {
							setValue("templateUuid", opt.find((x) => x.label === "Standard Template")?.value);
						}
					}}
				>
					{(options) => (
						<FormFields.Select
							control={control}
							formState={formState}
							name="templateUuid"
							label="Select template"
							options={options}
							floatingAppearance={{ className: "max-h-[328px]" }}
							data-qualifier={qualifier.reportCustomisation.modules.commentary.selectTemplate}
						/>
					)}
				</ReactQueryWrapper>
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="useTemplateLanguage"
					data-qualifier={qualifier.reportCustomisation.modules.commentary.enableTemplateLanguage}
				>
					Use template language
				</FormFields.Checkbox>
				<hr />
				<div className="flex flex-col gap-4">
					<FormFields.Text
						disabled={watch("useTemplateNameAsTitle")}
						control={control}
						formState={formState}
						name="title"
						label="Title"
						data-qualifier={qualifier.reportCustomisation.modules.commentary.title}
					/>
					<FormFields.Checkbox
						control={control}
						formState={formState}
						name="useTemplateNameAsTitle"
						data-qualifier={qualifier.reportCustomisation.modules.commentary.enableTemplateNameAsTitle}
					>
						Use template name as title
					</FormFields.Checkbox>
				</div>
			</div>
		</SideBarContent>
	);
}

export function Performance(props: SetConfigurationReportTemplateHelper<"performance">): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	const { t } = useTranslation();

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<div>
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ForEach collection={typedObjectKeys(opts)}>
					{(key) => (
						<div>
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name={key}
								className="mb-1"
								data-qualifier={qualifier.reportCustomisation.modules.performance.metric(key)}
							>
								{t(`REPORT_COMPONENTS.PERFORMANCE.${key}`)}
							</FormFields.Checkbox>
						</div>
					)}
				</ForEach>
			</div>
		</SideBarContent>
	);
}

const nonCustomAvailableCompositionColumns = [
	"NAME",
	"WEIGHT",
	"DESCRIPTION",
	"ALIAS",
	"CUSIP",
	"ISIN",
	"BLOOMBERG_TICKER",
] as const;
const nonCustomAvailableEnhanceCompositionColumns = [
	...nonCustomAvailableCompositionColumns,
	"DIFFERENCE",
	"ENHANCED_WEIGHT",
] as const;

const columnToLabel = {
	DESCRIPTION: "Description",
	DIFFERENCE: "Delta",
	ENHANCED_WEIGHT: "Enhanced Weight",
	NAME: "Name",
	WEIGHT: "Weight",
	ALIAS: "Alias",
	CUSIP: "CUSIP",
	ISIN: "ISIN",
	BLOOMBERG_TICKER: "Bloomberg ticker",
} satisfies Record<(typeof nonCustomAvailableEnhanceCompositionColumns)[number], string>;

function columnsToOption(
	columns: Partial<typeof nonCustomAvailableEnhanceCompositionColumns>,
): Array<OptionWithChildren<string>> {
	return columns.flatMap((c) =>
		c
			? [
					{
						label: columnToLabel[c],
						value: c,
					},
				]
			: [],
	);
}

export function Composition(props: SetConfigurationReportTemplateHelper<"composition">): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, watch, formState } = useForm({
		defaultValues: {
			currentColumnPreferences: props.templateVariant === "current" ? opts.currentColumnPreferences : [],
			proposalColumnPreferences: props.templateVariant === "proposal" ? opts.proposalColumnPreferences : [],
			decimalPlaces: opts.decimalPlaces ?? 2,
		},
	});
	/** use forms */
	const columnPreferences =
		props.templateVariant === "current" ? watch("currentColumnPreferences") : watch("proposalColumnPreferences");

	const currentColumnPreferencesOptionsControl = useFieldArray<
		{
			currentColumnPreferences: CompositionColumnPreference[];
			proposalColumnPreferences: CompositionColumnPreference[];
			decimalPlaces: number;
		},
		"currentColumnPreferences",
		"id"
	>({
		control,
		name: "currentColumnPreferences",
		keyName: "id",
	});

	const proposalColumnPreferencesOptionsControl = useFieldArray<
		{
			currentColumnPreferences: CompositionColumnPreference[];
			proposalColumnPreferences: CompositionColumnPreference[];
			decimalPlaces: number;
		},
		"proposalColumnPreferences",
		"id"
	>({
		control,
		name: "proposalColumnPreferences",
		keyName: "id",
	});

	const classificationController =
		props.templateVariant === "current"
			? currentColumnPreferencesOptionsControl
			: proposalColumnPreferencesOptionsControl;
	/** <!-- use forms --!> */

	const autocompleteHandleRef = useRef<AutocompleteHandle | null>(null);
	const instrumentsClassificationsControllerV1Api = useApiGen(InstrumentsClassificationsControllerV1ApiFactory);

	const queryUserClassifications = useQueryNoRefetch(["queryUserClassifications"], {
		queryFn: () => axiosExtract(instrumentsClassificationsControllerV1Api.retrieveAllClassifications()),
	});

	const autoCompleteClassificationOptions = useMemo(() => {
		const { data } = queryUserClassifications;
		if (!data) {
			return [];
		}

		const options = columnsToOption(
			props.templateVariant === "current"
				? nonCustomAvailableCompositionColumns
				: nonCustomAvailableEnhanceCompositionColumns,
		);

		return [
			...options,
			...data.flatMap((classification): OptionWithChildren<string>[] => {
				return [
					{
						label: classification.name ?? "-",
						value: classification.classificationUuid!,
					},
				];
			}),
		].filter((opt) => !columnPreferences.find((c) => c.preferenceType.classificationUuid === opt.value));
	}, [columnPreferences, props.templateVariant, queryUserClassifications]);

	const classificationsMap = useMemo<Map<string, UserInstrumentClassificationDto>>(() => {
		const { data } = queryUserClassifications;
		if (!data) {
			return Map();
		}
		const baseColumns =
			props.templateVariant === "current"
				? nonCustomAvailableCompositionColumns
				: nonCustomAvailableEnhanceCompositionColumns;

		const baseClassifications = baseColumns.map((x): [string, UserInstrumentClassificationDto] => [
			x,
			{ classificationUuid: x.toString(), name: columnToLabel[x] },
		]);

		const userClassificationsTuples = data.map((x): [string, UserInstrumentClassificationDto] => [
			x.classificationUuid!,
			x,
		]);

		return Map([...baseClassifications, ...userClassificationsTuples]);
	}, [props.templateVariant, queryUserClassifications]);

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => {
					props.onChange({ id, kind, ...payload });
				})();
			}}
			onCancel={props.onCancel}
		>
			<div>
				<FormFields.Stepper
					size="x-small"
					control={control}
					formState={formState}
					name="decimalPlaces"
					label="Decimals"
					min={2}
					max={4}
					className="mb-4 max-w-24"
				/>
				<Label htmlFor="" className="mb-px">
					Add columns
				</Label>
				<Autocomplete
					value={null}
					options={autoCompleteClassificationOptions}
					handleRef={autocompleteHandleRef}
					onChange={(classificationUuid) => {
						const classification = classificationUuid != null ? classificationsMap.get(classificationUuid) : undefined;
						if (classification) {
							classificationController.append({
								enabled: true,
								preferenceType: {
									standardClassificationId: classification.standardClassificationId,
									classificationUuid: classification.classificationUuid!,
									label: classification.name!,
								},
							});
						}
						autocompleteHandleRef.current?.clear();
					}}
					className="mb-4"
					size="small"
				>
					<Text as="div" type="Body/S/Medium" className="py-2 px-2.5">
						{autoCompleteClassificationOptions.length > 0 ? "Select an option" : "No more columns available"}
					</Text>
				</Autocomplete>
				<DraggableList
					items={columnPreferences}
					itemKey={(item) => item.preferenceType.classificationUuid}
					onReorder={(items) => classificationController.replace(items)}
				>
					{({ dragHandleProps, isDragging, draggableProps, item, innerRef, index }) => (
						<div
							style={{ borderColor: themeCSSVars.palette_N50 }}
							className={toClassName({
								"relative flex flex-row items-center min-w-0 bg-white transition-shadow border-b gap-2 px-2 h-8": true,
								"shadow-lg": isDragging,
								"border-t": isDragging,
							})}
							{...draggableProps}
							ref={innerRef}
						>
							<div {...dragHandleProps} className={toClassName({ invisible: !draggableProps || !dragHandleProps })}>
								<svg
									role="presentation"
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M6.04181 3.2944L7.96096 1.37506C8.01677 1.31944 8.10704 1.31944 8.16286 1.37506L10.082 3.2944C10.1228 3.3352 10.1349 3.3966 10.113 3.44991C10.0909 3.50323 10.0389 3.53807 9.98115 3.53807H6.14267C6.08493 3.53807 6.03277 3.50323 6.01083 3.44991C5.98888 3.3966 6.00101 3.3352 6.04181 3.2944ZM8.16305 14.8297C8.10724 14.8853 8.01697 14.8853 7.96115 14.8297L6.04181 12.9103C6.00101 12.8695 5.98888 12.8081 6.01083 12.7548C6.03296 12.7015 6.08512 12.6667 6.14286 12.6667H9.98134C10.0391 12.6667 10.091 12.7015 10.1132 12.7548C10.1351 12.8081 10.123 12.8695 10.0822 12.9103L8.16305 14.8297ZM14.3446 6.00001H1.97822C1.62186 6.00001 1.33337 6.29415 1.33337 6.65052C1.33337 7.00688 1.33337 7.30102 1.97822 7.30102H14.3333C14.701 7.30102 14.9895 7.01254 14.9895 6.65052C14.9895 6.28849 14.701 6.00001 14.3446 6.00001ZM1.97822 9.33334H14.3446C14.701 9.33334 14.9895 9.62183 14.9895 9.98385C14.9895 10.3459 14.701 10.6344 14.3333 10.6344H1.97822C1.33337 10.6344 1.33337 10.3402 1.33337 9.98385C1.33337 9.62748 1.62186 9.33334 1.97822 9.33334Z"
										fill="#8792AB"
									/>
								</svg>
							</div>
							<Text type="Body/M/Book">
								{/* workaround :( */}
								{(columnToLabel as Partial<Record<string, string>>)[item.preferenceType.classificationUuid] ??
									item.preferenceType.label}
							</Text>
							<IconButton
								icon="Delete"
								onClick={() => classificationController.remove(index)}
								className="ml-auto"
								color={themeCSSVars.palette_P400}
							/>
						</div>
					)}
				</DraggableList>
			</div>
		</SideBarContent>
	);
}

export function PerformanceAttribution(
	props: SetConfigurationReportTemplateHelper<"performanceAttribution">,
): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;

	const { t } = useTranslation();
	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<div>
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ForEach collection={typedObjectKeys(opts)}>
					{(key) => (
						<div>
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name={key}
								className="mb-1"
								data-qualifier={qualifier.reportCustomisation.modules.performanceAttribution.horizion(key)}
							>
								{t(`FORECAST_HORIZON.${key}`)}
							</FormFields.Checkbox>
						</div>
					)}
				</ForEach>
			</div>
		</SideBarContent>
	);
}

export function PerformanceAndVolatilityContribution(
	props: SetConfigurationReportTemplateHelper<"performanceAndVolatilityContribution">,
): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;

	const { t } = useTranslation();
	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<div className="mb-4">
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="filterSignificant"
					className="mb-1"
					data-qualifier={
						qualifier.reportCustomisation.modules.performanceAndVolatilityContribution.toggleSignificantValueOnly
					}
				>
					Significant values only
				</FormFields.Checkbox>
			</div>

			<div>
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ForEach collection={typedObjectKeys(opts.categories)}>
					{(key) => (
						<div>
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name={`categories.${key}`}
								className="mb-1"
								data-qualifier={qualifier.reportCustomisation.modules.performanceAndVolatilityContribution.views(key)}
							>
								{t(`CATEGORIES.${key}`)}
							</FormFields.Checkbox>
						</div>
					)}
				</ForEach>
			</div>
		</SideBarContent>
	);
}

export function ExAnteContributionVolatility(
	props: SetConfigurationReportTemplateHelper<"exAnteContributionVolatility">,
): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;

	const { t } = useTranslation();
	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<div className="mb-4">
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="filterSignificant"
					className="mb-1"
					data-qualifier={qualifier.reportCustomisation.modules.exAnteContributionVolatility.toggleSignificantValueOnly}
				>
					Significant values only
				</FormFields.Checkbox>
			</div>

			<div>
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ForEach collection={typedObjectKeys(opts.categories)}>
					{(key) => (
						<div>
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name={`categories.${key}`}
								className="mb-1"
								data-qualifier={qualifier.reportCustomisation.modules.exAnteContributionVolatility.views(key)}
							>
								{t(`CATEGORIES.${key}`)}
							</FormFields.Checkbox>
						</div>
					)}
				</ForEach>
			</div>
		</SideBarContent>
	);
}

export function FactorExposure(props: SetConfigurationReportTemplateHelper<"factorExposure">): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;

	const { control, handleSubmit, formState } = useForm({
		defaultValues: opts,
	});

	const { t } = useTranslation();

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<div>
				<p className="mb-4 font-semibold">Select the elements you want to display</p>
				<ForEach collection={typedObjectKeys(opts)}>
					{(key) => (
						<div>
							<FormFields.Checkbox
								control={control}
								formState={formState}
								name={key}
								className="mb-1"
								data-qualier={qualifier.reportCustomisation.modules.factorExposure.factors(key)}
							>
								{t(`REPORT_COMPONENTS.FACTORSEXPOSURE.${key}`)}
							</FormFields.Checkbox>
						</div>
					)}
				</ForEach>
			</div>
		</SideBarContent>
	);
}

const defaultVersusOption = [
	"MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
	"MACRO_GEOGRAPHY_VS_MICRO_GEOGRAPHY",
	"MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	"MACRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY",
	"MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY",
	"MICRO_ASSET_CLASS_VS_MICRO_GEOGRAPHY",
	"CURRENCY",
] satisfies InvestmentExposureResponseExposureTypeEnum[];
export function Exposure(props: SetConfigurationReportTemplateHelper<"exposure">): React.JSX.Element {
	const { kind, id, selectedCategories: initialSelectedCategories } = props.configuration;
	const { t } = useTranslation();
	const [selectedCategories, setSelectedCategories] = useState(Map(initialSelectedCategories.map((x) => [x.value, x])));

	const instrumentsClassificationsControllerV1Api = useApiGen(InstrumentsClassificationsControllerV1ApiFactory);
	const queryUserClassifications = useQueryNoRefetch(["queryUserClassifications"], {
		queryFn() {
			return axiosExtract(instrumentsClassificationsControllerV1Api.retrieveAllClassifications());
		},
	});

	const tagClassifications = useMemo(() => {
		const classifications = queryUserClassifications.data;
		if (!classifications) {
			return [];
		}

		return classifications.filter((classification) => classification.fieldType === "TAG");
	}, [queryUserClassifications.data]);

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={() => {
				props.onChange({ id, kind, selectedCategories: selectedCategories.valueSeq().toArray() });
			}}
			onCancel={props.onCancel}
		>
			<div>
				<Text type="Body/M/Bold" className="mb-4" as="p">
					Select exposure charts you want to display
				</Text>

				<Text type="Body/M/Medium" className="mb-2" as="p">
					Risk model
				</Text>

				<ForEach collection={defaultVersusOption}>
					{(key) => (
						<div>
							<Checkbox
								checked={selectedCategories.has(key)}
								onChange={() => {
									if (selectedCategories.has(key)) {
										setSelectedCategories((latest) => latest.remove(key));
										return;
									}

									setSelectedCategories((latest) =>
										latest.set(key, {
											label: key,
											type: "RISK_MODEL",
											value: key,
										}),
									);
								}}
							>
								{t(`CATEGORIES.${key}`)}
							</Checkbox>
						</div>
					)}
				</ForEach>

				<Text type="Body/M/Medium" className="mt-4 mb-2" as="p">
					Tag
				</Text>
				<ForEach collection={tagClassifications}>
					{(item) => (
						<div>
							<Checkbox
								checked={selectedCategories.has(item.classificationUuid!)}
								onChange={() => {
									if (selectedCategories.has(item.classificationUuid!)) {
										setSelectedCategories((latest) => latest.remove(item.classificationUuid!));
										return;
									}

									setSelectedCategories((latest) =>
										latest.set(item.classificationUuid!, {
											label: item.name ?? "untitled",
											type: "TAG",
											value: item.classificationUuid!,
										}),
									);
								}}
							>
								{item.name}
							</Checkbox>
						</div>
					)}
				</ForEach>
			</div>
		</SideBarContent>
	);
}

export function BondsDividendsTracker(
	props: SetConfigurationReportTemplateHelper<"bondsDividendsTracker">,
): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, formState, watch } = useForm({ defaultValues: opts });

	const sections = [
		{ key: "history", label: "History" },
		{ key: "upcoming", label: "Upcoming" },
	] as const;

	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<p className="mb-4 font-semibold">Select the sections you want to display</p>
			<ForEach collection={sections}>
				{(section) => (
					<div className="mb-4">
						<FormFields.Checkbox
							control={control}
							formState={formState}
							name={`${section.key}.enabled`}
							className="mb-2"
							data-qualifier={qualifier.reportCustomisation.modules.bondsDividendsTracker.toggleSection(section.key)}
						>
							{section.label}
						</FormFields.Checkbox>
						<FormFields.Stepper
							size="x-small"
							control={control}
							formState={formState}
							name={`${section.key}.maxItems`}
							label="Show at most"
							min={1}
							max={50}
							disabled={!watch(`${section.key}.enabled`)}
							className="max-w-24"
							data-qualifier={qualifier.reportCustomisation.modules.bondsDividendsTracker.maxItems(section.key)}
						/>
					</div>
				)}
			</ForEach>
		</SideBarContent>
	);
}

export function MaturityCouponTracker(
	props: SetConfigurationReportTemplateHelper<"maturityCouponTracker">,
): React.JSX.Element {
	const { kind, id, ...opts } = props.configuration;
	const { control, handleSubmit, formState } = useForm({ defaultValues: opts });

	// Per-bucket toggles are not offered here: the buckets are portfolio-scoped, but templates are
	// edited without a portfolio, so each section is simply shown or hidden as a whole.
	return (
		<SideBarContent
			title={itemToLabelMap[kind]}
			onSubmit={async () => {
				await handleSubmit((payload) => props.onChange({ id, kind, ...payload }))();
			}}
			onCancel={props.onCancel}
		>
			<p className="mb-4 font-semibold">Select the sections you want to display</p>

			<div className="mb-4">
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="maturity.enabled"
					className="mb-2"
					data-qualifier={qualifier.reportCustomisation.modules.maturityCouponTracker.toggleSection("maturity")}
				>
					Maturity date
				</FormFields.Checkbox>
			</div>

			<div className="mb-4">
				<FormFields.Checkbox
					control={control}
					formState={formState}
					name="coupon.enabled"
					className="mb-2"
					data-qualifier={qualifier.reportCustomisation.modules.maturityCouponTracker.toggleSection("coupon")}
				>
					Coupon date
				</FormFields.Checkbox>
			</div>
		</SideBarContent>
	);
}
